import { Box } from '@mui/material';
import classNames from 'classnames';
import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Dialog from '/@/components/dialog';
import InteractiveFieldOverlayList from '/@/components/field-overlay-list/interactive';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { deleteFieldFromAPI } from '/@/store/fields/apiProviders';
import { getSelectedFeature } from '/@/store/nav/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import { isMobile } from '/@/utils';
import FieldCard from './field-card';
import styles from './styles.module.css';

interface FieldInfoProps {
    field: Field;
    shouldCollapse?: boolean;
    padding?: boolean;
    onClick?: () => void;
    onDelete?: () => void;
    disabled?: boolean;
    view?: string;
    showIconMap?: boolean;
    showMap?: boolean;
    onClickShowMap?: () => void;
    showArrow?: boolean;
    edit?: () => void;
    showActions?: () => void;
    selectedField?: unknown;
    hideOverlays?: boolean;
    selected?: unknown;
    isPdf?: boolean;
    disabledErrorMessage?: unknown;
    showCompleteInfo?: unknown;
}

const FieldInfo: React.FC<FieldInfoProps> = ({
    field,
    shouldCollapse,
    padding = true,
    onClick,
    onDelete,
    disabled,
    view,
    showIconMap,
    showMap,
    onClickShowMap,
    showArrow = true,
    edit,
    selectedField,
    hideOverlays = false,
    selected,
    disabledErrorMessage,
    showCompleteInfo = true,
    showActions,
    children,
    isPdf = false,
}) => {
    const [collapsible, setCollapsible] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showActionsMobile, setShowActionsMobile] = useState(false);

    const selectedFeature = useSelector(getSelectedFeature);
    const country = useSelector(getSessionCountry);

    useEffect(() => {
        configureCollapsible({ shouldCollapse });
    }, [shouldCollapse]);

    const handleEdit = (event) => {
        event.stopPropagation();
        if (edit) edit();
    };

    const renderShowMap = () => {
        return (
            <Fragment>
                {isMobile() && (
                    <i onClick={onClickShowMap} className='material-icons'>
                        {showMap ? 'location_off' : 'location_on'}
                    </i>
                )}
            </Fragment>
        );
    };

    const renderInfo = () => {
        const rootClassNames = classNames(
            styles.root,
            selected && styles.selected,
            padding && styles.padding,
            (onClick || collapsible) && styles.clickable,
            disabled && styles.disabled,
            view === 'weatherPhone' && styles.weather,
            view === 'sidebar' && styles.sidebar
        );
        const isSelected = field?.id === selectedField?.id;
        return (
            <div className={rootClassNames} onClick={handleClick}>
                {disabled && <div className={styles.disabledLabel}>{disabledErrorMessage}</div>}
                <div className={styles.header}>
                    <Box
                        className={styles.info}
                        sx={[
                            isSelected && {
                                '& .subtitle': {
                                    color: 'inherit',
                                },
                            },
                        ]}
                    >
                        <FieldCard
                            name={field.name}
                            isFieldview={field.externalProvider === 'fieldview'}
                            location={field.location}
                            area={field.area}
                            altitude={field.altitude}
                            geometry={field.geometry}
                            selected={isSelected}
                        />
                    </Box>
                    <div className={styles.icons}>
                        {collapsible && (
                            <i className='material-icons'>{collapsed ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}</i>
                        )}
                        {onClick && showArrow && <i className='material-icons'>chevron_right</i>}
                        <div className={styles.icon}>{showIconMap && renderShowMap()}</div>
                    </div>
                </div>
                {!collapsed && (
                    <Fragment>
                        {children}
                        {!hideOverlays && renderOverlays()}
                    </Fragment>
                )}
                {onDelete && (
                    <Dialog
                        title={_('Eliminar lote')}
                        message={`${_('Estás seguro de que querés eliminar el lote')} "${field.name}"? ${_(
                            'Eliminando el lote se eliminarán todas las recomendaciones asociadas al mismo.'
                        )}`}
                        visible={showDeleteConfirmation}
                        onClose={handleDeleteCancel}
                        onCancel={handleDeleteCancel}
                        onAccept={handleDeleteAccept}
                        alignCenter
                    />
                )}
            </div>
        );
    };

    const renderActions = () => {
        const isSelected = field?.id === selectedField?.id;
        return (
            <div>
                <div className={classNames(styles.fieldDetailView)} onClick={showActions}>
                    <Box
                        className={styles.info}
                        sx={[
                            isSelected && {
                                '& .subtitle': {
                                    color: 'inherit',
                                },
                            },
                        ]}
                    >
                        <FieldCard
                            name={field.name}
                            isFieldview={field.externalProvider === 'fieldview'}
                            location={field.location}
                            area={field.area}
                            altitude={field.altitude}
                            geometry={field.geometry}
                            selected={isSelected}
                        />
                    </Box>
                    {selectedFeature.selectedFeature === 'FieldDetail' && isMobile() ? (
                        !showActionsMobile && (
                            <div className={classNames(styles.icons, styles.arrowRight)}>
                                <i className='material-icons'>chevron_right</i>
                            </div>
                        )
                    ) : (
                        <div className={styles.icons}>
                            <div
                                className={classNames(styles.moreInfoIcon, styles[`theme-${settings.app.theme}`])}
                                onClick={() => showActions()}
                            >
                                <span className='material-icons'>chevron_right</span>
                            </div>
                        </div>
                    )}
                    <Dialog
                        title={_('Eliminar lote')}
                        message={`${_('Estás seguro de que querés eliminar el lote')} "${field.name}"?`}
                        visible={showDeleteConfirmation}
                        onClose={handleDeleteCancel}
                        onCancel={handleDeleteCancel}
                        onAccept={handleDeleteAccept}
                        alignCenter
                    />
                </div>
                {showActionsMobile && (
                    <div className={styles.actionsMobile}>
                        <div onClick={(e) => handleEdit(e)} className={styles.editIcon}>
                            <span className='material-icons'>drive_file_rename_outline</span>
                        </div>
                        <div className={styles.deleteIcon} onClick={handleDelete}>
                            <span className='material-icons'>delete_outline</span>
                        </div>
                        <div className={styles.closeIcon} onClick={() => setShowActionsMobile(false)}>
                            <span className='material-icons'>close</span>
                        </div>
                    </div>
                )}
                <Dialog
                    title={_('Eliminar lote')}
                    message={`${_('Estás seguro de que querés eliminar el lote')} "${field.name}"? ${_(
                        'Eliminando el lote se eliminarán todas las recomendaciones asociadas al mismo.'
                    )}`}
                    visible={showDeleteConfirmation}
                    onClose={handleDeleteCancel}
                    onCancel={handleDeleteCancel}
                    onAccept={handleDeleteAccept}
                    alignCenter
                />
            </div>
        );
    };

    const renderOverlays = () => {
        return <InteractiveFieldOverlayList field={field} />;
    };

    const handleClick = () => {
        if (collapsible) setCollapsed((pV) => !pV);
        if (onClick) onClick();
    };

    const handleDelete = (event: React.MouseEvent<HTMLElement, MouseEvent> | undefined = undefined) => {
        event?.stopPropagation();
        setShowDeleteConfirmation(true);
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirmation(false);
    };

    const handleDeleteAccept = () => {
        if (selectedFeature.selectedFeature === 'FieldDetail') {
            deleteFieldFromAPI({ fieldId: field.id, country: country.code });

            handleDelete();
        } else {
            onDelete();
        }
    };

    const configureCollapsible = ({ shouldCollapse }) => {
        if (shouldCollapse) {
            const collapse = !!isMobile();

            setCollapsed(collapse);
            setCollapsible(collapse);
        }
    };
    return view !== 'Home' ? renderInfo() : renderActions();
};

export default FieldInfo;
